export type ISalesNavigatorProfile = {
    memberId?: string
    [key: string]: unknown
}

export type ISalesNavigatorData = {
    isPremium?: boolean
    userPlans?: string[]
    SNProfile?: ISalesNavigatorProfile | null
}

export type SalesNavigatorGuardReason = 'ok' | 'no_linkedin' | 'no_plan' | 'no_session'

/** Plan label set when SN inbox probe succeeds (matches `hasSalesNavigatorPlan` regex). */
export const SALES_NAVIGATOR_DETECTED_PLAN = 'Sales Navigator'

export const REGULAR_LINKEDIN_PLAN = 'regular'

/** Parity with linkedinextension `getUserSalesId()` → `salesProfile.memberId`. */
export const getSalesNavigatorMemberId = (data?: ISalesNavigatorData | null): string => {
    const profile = data?.SNProfile
    if (!profile || typeof profile !== 'object') return ''
    const memberId = (profile as ISalesNavigatorProfile).memberId
    return memberId != null && memberId !== '' ? String(memberId) : ''
}

export const hasSalesNavigatorPlan = (data?: ISalesNavigatorData | null) =>
    !!data?.userPlans?.some(
        plan => /Sales Navigator/gi.test(plan) || /Business Plus/gi.test(plan),
    )

/** Active SN session in extension storage (profile loaded, optional memberId). */
export const isSalesNavigatorSessionReady = (data?: ISalesNavigatorData | null) => {
    if (getSalesNavigatorMemberId(data)) return true
    return !!data?.SNProfile
}

/** Live `/me` premium from extension — not stale backend `linkedinData.isPremium`. */
export const resolveLinkedInIsPremium = (
    linkedinData?: { isPremium?: boolean } | null,
    sn?: ISalesNavigatorData | null,
): boolean => {
    if (sn?.isPremium != null) return !!sn.isPremium
    return !!linkedinData?.isPremium
}

/** Parity with linkedinextension `isSalesUser()` — premium + SN plan, no live session required. */
export const isSalesNavigatorPlanHolder = (linkedinData?: { isPremium?: boolean } | null, sn?: ISalesNavigatorData | null) => {
    const isPremium = resolveLinkedInIsPremium(linkedinData, sn)
    return !!linkedinData && isPremium && hasSalesNavigatorPlan(sn)
}

/** Same visibility rule as linkedinextension ConversationsList SN tab. */
export const canShowSalesNavigatorTab = (
    linkedinData?: { isPremium?: boolean } | null,
    sn?: ISalesNavigatorData | null,
) => isSalesNavigatorPlanHolder(linkedinData, sn)

export const canSyncSalesNavigatorFromExtension = (sn?: ISalesNavigatorData | null) =>
    hasSalesNavigatorPlan(sn) && isSalesNavigatorSessionReady(sn)

export const evaluateSalesNavigatorGuard = (linkedinData?: { isPremium?: boolean } | null, sn?: ISalesNavigatorData | null): SalesNavigatorGuardReason => {
    if (!linkedinData) return 'no_linkedin'
    if (!isSalesNavigatorPlanHolder(linkedinData, sn)) return 'no_plan'
    if (!isSalesNavigatorSessionReady(sn)) return 'no_session'
    return 'ok'
}

export const SALES_NAVIGATOR_GUARD_MESSAGE: Record<Exclude<SalesNavigatorGuardReason, 'ok'>, string> = {
    no_linkedin: 'Connect a LinkedIn account to use Sales Navigator.',
    no_plan: 'Sales Navigator or Business Plus plan required on your LinkedIn account.',
    no_session: 'Please log into your Sales Navigator and refresh the page to access this inbox.',
}
